import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnInit,
  output,
  TemplateRef,
} from '@angular/core';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import { DragDropItemInterface, DragDropOrientation } from '../../types';

@Component({
  selector: 'ui-drag-drop-list',
  imports: [CdkDropList, CdkDrag, NgTemplateOutlet],
  templateUrl: './drag-drop-list.component.html',
  styleUrl: './drag-drop-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DragDropListComponent<
  T extends DragDropItemInterface,
> implements OnInit {
  items = input<T[]>([]);
  savedOrder = input<string[]>([]);
  orientation = input<DragDropOrientation>('vertical');
  trackByField = input<string>('id');
  containerClass = input<string>('');
  itemClass = input<string>('');
  itemTemplate = input<TemplateRef<unknown>>();

  orderChanged = output<string[]>();
  itemDropped = output<CdkDragDrop<T[]>>();

  displayedItems: T[] = [];

  ngOnInit() {
    this.applyOrder();
  }

  private applyOrder() {
    if (!this.items().length) {
      this.displayedItems = [];
      return;
    }

    if (this.savedOrder() && this.savedOrder().length === this.items().length) {
      this.applySavedOrder(this.savedOrder());
    } else {
      this.displayedItems = [...this.items()];
      this.emitCurrentOrder();
    }
  }

  private applySavedOrder(order: string[]): void {
    const itemsMap = new Map(
      this.items().map((item) => [item[this.trackByField()] as string, item]),
    );
    const orderedItems: T[] = [];

    for (const id of order) {
      const item = itemsMap.get(id);
      if (item) {
        orderedItems.push(item);
        itemsMap.delete(id);
      }
    }

    for (const item of itemsMap.values()) {
      orderedItems.push(item);
    }

    this.displayedItems = orderedItems;
  }

  onDrop(event: CdkDragDrop<T[]>) {
    moveItemInArray(
      this.displayedItems,
      event.previousIndex,
      event.currentIndex,
    );
    this.itemDropped.emit(event);
    this.emitCurrentOrder();
  }

  private emitCurrentOrder(): void {
    const currentOrder = this.displayedItems.map(
      (item) => item[this.trackByField()] as string,
    );
    this.orderChanged.emit(currentOrder);
  }

  trackByFn(index: number, item: T): string {
    return item[this.trackByField()] as string;
  }
}
